import { TranslateService } from '@ngx-translate/core';
import { take, filter, finalize, switchMap, catchError, Observable, throwError, BehaviorSubject } from 'rxjs';

import { inject } from '@angular/core';
import {
  HttpEvent,
  HttpRequest,
  HttpHandlerFn,
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';

import { SessionService } from '@/app/core/services/session';

import { ToastService } from '@/app/shared/components/ui/toast';

export const ALLOW_REFRESH = new HttpContextToken<boolean>(() => true);

let isRefreshingToken = false;
const tokenSubject = new BehaviorSubject<string | null>(null);

export const refreshInterceptor: HttpInterceptorFn = (request, next) => {
  const toastService = inject(ToastService);
  const sessionsService = inject(SessionService);
  const translateService = inject(TranslateService);

  const addToken = (req: HttpRequest<unknown>, token: string): HttpRequest<unknown> => {
    return req.clone({
      headers: req.headers.set('Authorization', 'Bearer ' + token),
    });
  };

  const handle401Error = (request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    if (isRefreshingToken) {
      return tokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((jwt) => {
          return next(addToken(request, jwt));
        }),
      );
    } else {
      isRefreshingToken = true;
      tokenSubject.next(null);

      return sessionsService.refreshToken().pipe(
        catchError(() => {
          toastService.error(translateService.instant('common.session.refresh-error'));
          sessionsService.navigateToLogin();

          return throwError(
            () =>
              new HttpErrorResponse({
                error: 'Unauthorized',
                status: 401,
              }),
          );
        }),
        switchMap((credentialsInfo) => {
          const token: string | undefined = credentialsInfo.token.key;

          if (token) {
            tokenSubject.next(token);
            return next(addToken(request, token));
          }

          toastService.error(translateService.instant('common.session.refresh-error'));
          sessionsService.navigateToLogin();

          return throwError(
            () =>
              new HttpErrorResponse({
                error: 'Unauthorized',
                status: 401,
              }),
          );
        }),
        finalize(() => {
          isRefreshingToken = false;
        }),
      );
    }
  };

  return next(request).pipe(
    catchError((error) => {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 401 && request.context.get(ALLOW_REFRESH)) {
          return handle401Error(request, next);
        } else {
          return throwError(() => error);
        }
      } else {
        return throwError(() => error);
      }
    }),
  );
};
